// Tamil-Katral-Maiyam\src\pages\admin\ClassesList.tsx
//
// List, single-create, and CSV bulk import use the roster API (GET/POST /v1/roster/classes, POST /v1/roster/classes/bulk).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { ScrollableSelect } from '../../components/ui/ScrollableSelect';
import { Spinner } from '../../components/ui/Spinner';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { validateGradeName, validateGradeCode } from '../../utils/validation';
import {
  listClasses as listRosterClasses,
  createClass as createRosterClass,
  bulkCreateClasses as bulkCreateRosterClasses,
  getClass,
  patchClass,
  CreateClassPayload,
  RosterClass,
  listTeachers,
  assignTeacherToClass,
  unassignTeacherFromClass,
  RosterTeacher,
  type RosterBulkCreateClasses422Body,
  type RosterBulkCreateClassesResponse,
} from '../../modules/roster/api/roster.client';
import {
  buildRosterBulkPayloadFromParsed,
  parseBulkClassesCsv,
  type ParseBulkClassesCsvResult,
  type ParsedCsvRow,
} from './classBulkImportCsv';
import { EMPTY_FIELD, mapRosterClassToClassesListRow, type ClassesListRow } from './classesListRow';

import { ChevronLeft, ChevronRight, Eye, Pencil, UserPlus, UserMinus, Download } from 'lucide-react';

type YearPickerProps = {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onSelect: (year: string) => void;
};

const YearPickerModal: React.FC<YearPickerProps> = ({ isOpen, onClose, value, onSelect }) => {
  const ACADEMIC_START_MONTH = 6;

  const getCurrentAcademicStartYear = (now = new Date()) => {
    const m = now.getMonth() + 1;
    const y = now.getFullYear();
    return m >= ACADEMIC_START_MONTH ? y : y - 1;
  };

  const currentYear = getCurrentAcademicStartYear();
  const [baseYear, setBaseYear] = useState<number>(currentYear);

  useEffect(() => {
    if (isOpen) {
      setBaseYear(currentYear);
    }
  }, [isOpen, currentYear]);

  const years = useMemo(() => Array.from({ length: 12 }, (_, i) => baseYear + i), [baseYear]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Select Academic Year" size="sm">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          {(() => {
            const canGoPrev = baseYear > currentYear;
            return (
              <button
                type="button"
                disabled={!canGoPrev}
                className={[
                  'inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 p-2',
                  canGoPrev ? 'hover:bg-gray-50 dark:hover:bg-gray-800' : 'opacity-40 cursor-not-allowed',
                ].join(' ')}
                onClick={() => setBaseYear((y) => Math.max(currentYear, y - 12))}
                aria-label="Previous years"
                title={!canGoPrev ? 'Previous years disabled' : 'Previous years'}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            );
          })()}

          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {baseYear} – {baseYear + 11}
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 p-2 hover:bg-gray-50 dark:hover:bg-gray-800"
            onClick={() => setBaseYear((y) => y + 12)}
            aria-label="Next years"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {years.map((y) => {
            const isSelected = String(y) === value;
            const isDisabled = y < currentYear;

            return (
              <button
                key={y}
                type="button"
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) return;
                  onSelect(String(y));
                  onClose();
                }}
                className={[
                  'rounded-xl px-3 py-3 text-sm font-semibold transition border',
                  isSelected
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-white',
                  isDisabled ? 'opacity-40 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent' : '',
                ].join(' ')}
                title={isDisabled ? 'Previous years are disabled' : ''}
              >
                {y}
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const ClassesList: React.FC = () => {
  const [classes, setClasses] = useState<ClassesListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newClassForm, setNewClassForm] = useState<CreateClassPayload>({
    name: '',
    code: '',
    grade: '',
    academic_year_key: '',
  });
  const [addClassErrors, setAddClassErrors] = useState<Record<string, string>>({});

  const [detailClass, setDetailClass] = useState<RosterClass | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [teachers, setTeachers] = useState<RosterTeacher[]>([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignRole, setAssignRole] = useState<'primary' | 'secondary'>('primary');
  const [assignTeacherId, setAssignTeacherId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editClassId, setEditClassId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editNameError, setEditNameError] = useState('');
  const [isPatching, setIsPatching] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');

  const [isYearPickerOpen, setIsYearPickerOpen] = useState(false);

  const gradeNumberOptions = useMemo(
    () => [
      { label: 'Select grade…', value: '' },
      ...Array.from({ length: 12 }, (_, i) => {
        const n = i + 1;
        return { label: String(n), value: String(n) };
      }),
    ],
    []
  );

  const loadClasses = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listRosterClasses({ limit: 200, skip: 0 });
      setClasses(items.map(mapRosterClassToClassesListRow));
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadClasses();
  }, [loadClasses]);

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [parsedImport, setParsedImport] = useState<ParseBulkClassesCsvResult | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [continueOnError, setContinueOnError] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [importApiResult, setImportApiResult] = useState<RosterBulkCreateClassesResponse | null>(null);
  const [importPreflight422, setImportPreflight422] = useState<unknown[] | null>(null);

  const resetImportModalState = () => {
    setImportFile(null);
    setParsedImport(null);
    setImportApiResult(null);
    setImportPreflight422(null);
    setSkipDuplicates(true);
    setContinueOnError(true);
    setIsImporting(false);
  };

  const downloadSample = () => {
    const csv = `name,grade,academic_year_key,status
Grade 1,1,2026,active
Grade 2,2,2026,active
`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'classes-sample.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onImportFileChange = async (file: File | null) => {
    setImportFile(file);
    setImportApiResult(null);
    setImportPreflight422(null);
    if (!file) {
      setParsedImport(null);
      return;
    }
    try {
      const text = await file.text();
      setParsedImport(parseBulkClassesCsv(text));
    } catch {
      setParsedImport({
        rows: [],
        warnings: [],
        errors: ['Could not read the file.'],
      });
    }
  };

  const handleBulkImportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!parsedImport?.rows.length) {
      showErrorToast('Nothing to import.');
      return;
    }
    const blocking = [...parsedImport.errors];
    if (blocking.length) {
      showErrorToast(blocking[0] ?? 'Fix CSV errors before importing.');
      return;
    }
    setIsImporting(true);
    setImportPreflight422(null);
    try {
      const payload = buildRosterBulkPayloadFromParsed(parsedImport.rows, skipDuplicates, continueOnError);
      const res = await bulkCreateRosterClasses(payload);
      setImportApiResult(res);
      if (res.summary.created > 0) {
        await loadClasses();
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 422) {
        const data = err.response.data as RosterBulkCreateClasses422Body;
        const list = Array.isArray(data?.errors) ? data.errors : [data];
        setImportPreflight422(list);
        return;
      }
      showErrorToast(err);
    } finally {
      setIsImporting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const list = await listTeachers({ limit: 200, skip: 0 });
        setTeachers(list);
      } catch {
        // ignore
      }
    };
    if (detailClass || isAssignModalOpen) void load();
  }, [detailClass, isAssignModalOpen]);

  const teacherNameByPlatformId = useMemo(() => {
    const map: Record<string, string> = {};
    teachers.forEach((t) => {
      if (t.platform_user_id) {
        const name = t.display_name || `${t.first_name || ''} ${t.last_name || ''}`.trim();
        map[t.platform_user_id] = name || t.email || 'Teacher';
      }
    });
    return map;
  }, [teachers]);

  const openDetail = async (classId: string) => {
    setDetailLoading(true);
    try {
      const data = await getClass(classId);
      setDetailClass(data);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const openEditModal = (item: ClassesListRow) => {
    setEditClassId(item.id);
    setEditName(item.name ?? '');
    setEditNameError('');
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editClassId) return;
    const nameResult = validateGradeName(editName.trim());
    if (!nameResult.valid) {
      setEditNameError(nameResult.message ?? 'Name must contain at least one letter');
      if (nameResult.message) showErrorToast(nameResult.message);
      return;
    }
    setEditNameError('');
    setIsPatching(true);
    try {
      await patchClass(editClassId, { name: editName.trim() });
      showSuccessToast('Class updated.');
      setIsEditModalOpen(false);
      setEditClassId(null);
      setEditName('');
      await loadClasses();
      if (detailClass?.id === editClassId) {
        const updated = await getClass(editClassId);
        setDetailClass(updated);
      }
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsPatching(false);
    }
  };

  const normalizeGrade = (grade: string | null | undefined): string => {
    if (!grade || grade === EMPTY_FIELD || !String(grade).trim()) return '';
    const s = String(grade).trim();
    const num = parseInt(s, 10);
    if (!Number.isNaN(num)) return String(num);
    const match = s.match(/\d+/);
    return match ? match[0] : s;
  };

  const filterGradeOptions = useMemo(() => {
    const normalized = new Map<string, string>();
    classes.forEach((c) => {
      if (c.grade === EMPTY_FIELD) return;
      const canon = normalizeGrade(c.grade);
      if (canon) normalized.set(canon, canon);
    });
    const sorted = Array.from(normalized.keys()).sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));
    return [{ label: 'All grades', value: '' }, ...sorted.map((value) => ({ label: value, value }))];
  }, [classes]);

  const filterStatusOptions = useMemo(
    () => [
      { label: 'All statuses', value: '' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ],
    []
  );

  const filteredClasses = useMemo(() => {
    let list = classes;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          (c.name?.toLowerCase().includes(q) ?? false) ||
          (c.grade?.toLowerCase().includes(q) ?? false) ||
          (c.codeLabel.toLowerCase().includes(q) ?? false) ||
          (c.academicYearLabel.toLowerCase().includes(q) ?? false)
      );
    }
    if (filterGrade) list = list.filter((c) => normalizeGrade(c.grade) === filterGrade);
    if (filterStatus) list = list.filter((c) => c.statusLabel.toLowerCase() === filterStatus.toLowerCase());
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      from.setHours(0, 0, 0, 0);
      list = list.filter((c) => c.createdAtRaw && new Date(c.createdAtRaw) >= from);
    }
    return list;
  }, [classes, searchQuery, filterGrade, filterStatus, filterDateFrom]);

  const hasActiveFilters = Boolean(searchQuery.trim() || filterGrade || filterStatus || filterDateFrom);
  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterGrade('');
    setFilterStatus('');
    setFilterDateFrom('');
  };

  const handleCreateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAddClassErrors({});
    const errors: Record<string, string> = {};
    const nameResult = validateGradeName(newClassForm.name ?? '');
    if (!nameResult.valid) errors.name = nameResult.message ?? 'Name must contain at least one letter';
    const codeVal = (newClassForm.code ?? '').trim();
    if (codeVal) {
      const codeResult = validateGradeCode(newClassForm.code ?? '');
      if (!codeResult.valid) errors.code = codeResult.message ?? 'Code must be G1 through G12 (e.g. G2, G10)';
    }
    if (!newClassForm.grade?.trim()) errors.grade = 'Select a grade.';
    if (Object.keys(errors).length > 0) {
      setAddClassErrors(errors);
      const firstMessage = Object.values(errors)[0];
      if (firstMessage) showErrorToast(firstMessage);
      return;
    }
    setIsCreating(true);
    try {
      await createRosterClass({
        name: newClassForm.name.trim(),
        ...(codeVal ? { code: codeVal } : {}),
        grade: newClassForm.grade?.trim(),
        ...(newClassForm.academic_year_key?.trim()
          ? { academic_year_key: newClassForm.academic_year_key.trim() }
          : {}),
      });
      showSuccessToast('Class created.');
      setIsAddModalOpen(false);
      setAddClassErrors({});
      setNewClassForm({ name: '', code: '', grade: '', academic_year_key: '' });
      await loadClasses();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsCreating(false);
    }
  };

  const detailRows = useMemo(
    () =>
      detailClass
        ? [
            { label: 'Name', value: detailClass.name },
            { label: 'Grade', value: detailClass.grade ?? '—' },
            { label: 'Code', value: detailClass.code ?? '—' },
            { label: 'Academic year', value: detailClass.academic_year_key ?? '—' },
            { label: 'Status', value: detailClass.status },
          ]
        : [],
    [detailClass]
  );

  const openAssignModal = (role: 'primary' | 'secondary') => {
    setAssignRole(role);
    setAssignTeacherId('');
    setIsAssignModalOpen(true);
  };

  const handleAssignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailClass?.id || !assignTeacherId.trim()) return;
    setIsAssigning(true);
    try {
      await assignTeacherToClass(detailClass.id, {
        teacher_profile_id: assignTeacherId,
        role: assignRole,
      });
      showSuccessToast(`Teacher assigned as ${assignRole === 'primary' ? 'Primary' : 'Secondary'}.`);
      setIsAssignModalOpen(false);
      setAssignTeacherId('');
      const updated = await getClass(detailClass.id);
      setDetailClass(updated);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassign = async (teacherPlatformUserId: string) => {
    if (!detailClass?.id) return;
    try {
      await unassignTeacherFromClass(detailClass.id, teacherPlatformUserId);
      showSuccessToast('Teacher unassigned.');
      const updated = await getClass(detailClass.id);
      setDetailClass(updated);
    } catch (error) {
      showErrorToast(error);
    }
  };

  const placeholders = {
    name: 'Enter class name (e.g. Grade 1)',
    code: 'Enter class code (e.g. G2)',
    academic_year_key: 'Select year (e.g. 2026)',
  };

  const importReady =
    parsedImport &&
    parsedImport.errors.length === 0 &&
    parsedImport.rows.length > 0 &&
    !isImporting;

  return (
    <Layout>
      <PageHeader
        title="Classes"
        subtitle="Manage school classes"
        actions={
          <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
            <Button
              variant="outline"
              onClick={() => {
                resetImportModalState();
                setIsImportModalOpen(true);
              }}
            >
              Import classes
            </Button>
            <Button onClick={() => setIsAddModalOpen(true)}>Add class</Button>
          </div>
        }
      />

      <Card>
        <CardContent padding="md">
          <div className="mb-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-full min-w-0 sm:w-64 flex-1 sm:flex-initial">
                <Input
                  placeholder="Search name, grade, code, year…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="w-full min-w-0 sm:w-36">
                <ScrollableSelect
                  options={filterGradeOptions}
                  value={filterGrade}
                  onChange={(v) => setFilterGrade(v)}
                  placeholder="All grades"
                  maxListHeight="10rem"
                />
              </div>
              <div className="w-full min-w-0 sm:w-36">
                <Select
                  options={filterStatusOptions}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                />
              </div>
              <div className="w-full min-w-0 sm:w-40">
                <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
              </div>
              {hasActiveFilters && (
                <Button type="button" variant="outline" size="sm" onClick={clearAllFilters} className="shrink-0">
                  Clear
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Grade</TableHead>
                    {/* <TableHead>Code</TableHead> */}
                    <TableHead>Academic year</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClasses.map((item) => (
                    <TableRow key={item.id} onClick={() => void openDetail(item.id)}>
                      <TableCell className="font-medium text-gray-900 dark:text-white">{item.name}</TableCell>
                      <TableCell>{item.grade}</TableCell>
                      {/* <TableCell>{item.codeLabel}</TableCell> */}
                      <TableCell>{item.academicYearLabel}</TableCell>
                      <TableCell>{item.statusLabel}</TableCell>
                      <TableCell>{item.createdAtLabel}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            onClick={(event) => {
                              event.stopPropagation();
                              void openDetail(item.id);
                            }}
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditModal(item);
                            }}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setAddClassErrors({});
        }}
        title="Add class"
      >
        <form className="space-y-4" onSubmit={handleCreateSubmit}>
          <Input
            label="Name"
            required
            placeholder={placeholders.name}
            value={newClassForm.name}
            error={addClassErrors.name}
            onChange={(event) => {
              const v = event.target.value;
              setNewClassForm((prev) => ({ ...prev, name: v }));
              const result = validateGradeName(v);
              setAddClassErrors((prev) => (result.valid ? { ...prev, name: '' } : { ...prev, name: result.message ?? '' }));
            }}
          />

          <Input
            label="Code"
            placeholder={placeholders.code}
            value={newClassForm.code}
            error={addClassErrors.code}
            onChange={(event) => {
              const v = event.target.value;
              setNewClassForm((prev) => ({ ...prev, code: v }));
              if (!v.trim()) setAddClassErrors((prev) => ({ ...prev, code: '' }));
              else {
                const result = validateGradeCode(v);
                setAddClassErrors((prev) => (result.valid ? { ...prev, code: '' } : { ...prev, code: result.message ?? '' }));
              }
            }}
          />

          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Grade</p>
            <ScrollableSelect
              options={gradeNumberOptions}
              value={newClassForm.grade ?? ''}
              onChange={(v) => setNewClassForm((prev) => ({ ...prev, grade: v }))}
              placeholder="Select grade…"
              maxListHeight="10rem"
              className="mt-1"
            />
            {addClassErrors.grade ? <p className="mt-1 text-xs text-error-600">{addClassErrors.grade}</p> : null}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Academic year</p>
            <div className="mt-1">
              <Input
                label=""
                placeholder={placeholders.academic_year_key}
                value={newClassForm.academic_year_key ?? ''}
                readOnly
                onClick={() => setIsYearPickerOpen(true)}
                disabled={isCreating}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setIsAddModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isCreating}>
              Create
            </Button>
          </div>
        </form>
      </Modal>

      <YearPickerModal
        isOpen={isYearPickerOpen}
        onClose={() => setIsYearPickerOpen(false)}
        value={newClassForm.academic_year_key ?? ''}
        onSelect={(year) => setNewClassForm((prev) => ({ ...prev, academic_year_key: year }))}
      />

      <Modal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          resetImportModalState();
        }}
        title="Import classes"
        size="lg"
      >
        <form className="space-y-5" onSubmit={handleBulkImportSubmit}>
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <p className="font-medium">Instructions</p>
            <ol className="list-decimal list-inside text-sm space-y-1">
              <li>Download the sample CSV. Required columns: <strong>name</strong>, <strong>grade</strong>.</li>
              <li>
                Optional: <strong>academic_year_key</strong>, <strong>status</strong> (omit status to default to active
                on the server).
              </li>
              <li>
                Header <strong>academic_year</strong> is accepted as an alias for <strong>academic_year_key</strong>{' '}
                (e.g. old exports).
              </li>
              <li>
                Columns such as <strong>code</strong> or <strong>section</strong> are ignored for bulk import (use single
                class create to set code).
              </li>
              <li>Preview rows, then run import. Results stay visible until you close this dialog.</li>
            </ol>
          </div>

          <details className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <summary className="cursor-pointer font-medium text-gray-900 dark:text-white">Advanced options</summary>
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                />
                <span>Skip duplicates (default: on)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={continueOnError}
                  onChange={(e) => setContinueOnError(e.target.checked)}
                />
                <span>Continue on error (default: on)</span>
              </label>
            </div>
          </details>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Sample CSV</label>
              <button
                type="button"
                onClick={downloadSample}
                className="h-11 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <Download className="h-5 w-5" />
                Download sample
              </button>
            </div>
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">CSV file</label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="w-full sm:w-auto">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => void onImportFileChange(e.target.files?.[0] ?? null)}
                    className="hidden"
                  />
                  <span className="inline-flex w-full sm:w-auto h-11 items-center justify-center rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 text-sm font-medium text-gray-800 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                    Choose file
                  </span>
                </label>
                <div className="text-sm text-gray-600 dark:text-gray-300 truncate w-full sm:max-w-[220px]">
                  {importFile ? importFile.name : 'No file chosen'}
                </div>
              </div>
            </div>
          </div>

          {parsedImport?.warnings.length ? (
            <div className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 rounded-lg px-3 py-2">
              {parsedImport.warnings.map((w, i) => (
                <p key={i}>{w}</p>
              ))}
            </div>
          ) : null}

          {parsedImport && parsedImport.errors.length > 0 ? (
            <div className="text-sm text-error-700 dark:text-error-300 bg-error-50 dark:bg-error-950/30 rounded-lg px-3 py-2 space-y-1">
              {parsedImport.errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          ) : null}

          {parsedImport && parsedImport.rows.length > 0 && parsedImport.errors.length === 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-white">Preview ({parsedImport.rows.length} rows)</p>
              <div className="overflow-x-auto max-h-48 border border-gray-200 dark:border-gray-700 rounded-lg">
                <Table className="min-w-[480px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>academic_year_key</TableHead>
                      <TableHead>status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedImport.rows.map((r: ParsedCsvRow) => (
                      <TableRow key={r.sourceRowIndex}>
                        <TableCell>{r.sourceRowIndex}</TableCell>
                        <TableCell>{r.item.name}</TableCell>
                        <TableCell>{r.item.grade}</TableCell>
                        <TableCell>{r.item.academic_year_key ?? '—'}</TableCell>
                        <TableCell>{r.item.status ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}

          {importPreflight422 ? (
            <div className="text-sm text-error-700 dark:text-error-300 bg-error-50 dark:bg-error-950/30 rounded-lg px-3 py-2 space-y-1">
              <p className="font-medium">Import rejected (422)</p>
              {importPreflight422.map((e, i) => (
                <p key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</p>
              ))}
            </div>
          ) : null}

          {importApiResult ? (
            <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Import results</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Requested: {importApiResult.summary.requested} · Created: {importApiResult.summary.created} · Skipped:{' '}
                {importApiResult.summary.skipped} · Failed: {importApiResult.summary.failed}
              </p>
              <div className="overflow-x-auto max-h-56">
                <Table className="min-w-[560px] text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Class ID</TableHead>
                      <TableHead>Detail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importApiResult.results.map((row, idx) => (
                      <TableRow
                        key={idx}
                        className={
                          row.status === 'created'
                            ? 'bg-green-50/80 dark:bg-green-950/20'
                            : row.status === 'failed'
                              ? 'bg-red-50/80 dark:bg-red-950/20'
                              : 'bg-amber-50/50 dark:bg-amber-950/10'
                        }
                      >
                        <TableCell>{row.index}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.name ?? '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{row.class_id ?? '—'}</TableCell>
                        <TableCell>{row.message ?? row.error_code ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              className="w-full sm:w-auto h-11"
              onClick={() => {
                setIsImportModalOpen(false);
                resetImportModalState();
              }}
            >
              Close
            </Button>
            <Button type="submit" isLoading={isImporting} disabled={!importReady} className="w-full sm:w-auto h-11">
              Import
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(detailClass)}
        onClose={() => setDetailClass(null)}
        title={detailClass?.name || 'Class details'}
        size="md"
      >
        {detailLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : detailClass ? (
          <div className="space-y-4">
            <div className="space-y-2 text-sm">
              {detailRows.map((row) => (
                <p key={row.label} className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-600 dark:text-gray-400">{row.label}:</span>{' '}
                  <span className="text-gray-900 dark:text-white">{row.value ?? '—'}</span>
                </p>
              ))}
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Teachers</h3>
              <div className="space-y-3 text-sm min-w-0">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <div className="text-slate-600 dark:text-slate-300 sm:col-span-2">Primary:</div>
                  <div className="min-w-0 sm:col-span-7 font-medium text-gray-900 dark:text-white break-words">
                    {detailClass.primary_teacher_platform_user_id
                      ? teacherNameByPlatformId[detailClass.primary_teacher_platform_user_id] ?? '—'
                      : 'Not assigned'}
                  </div>
                  <div className="sm:col-span-3 flex sm:justify-end gap-2 flex-wrap">
                    {detailClass.primary_teacher_platform_user_id ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                        title="Unassign primary"
                        onClick={() => void handleUnassign(detailClass.primary_teacher_platform_user_id!)}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-10 rounded-xl flex items-center justify-center gap-1.5 shrink-0"
                      onClick={() => openAssignModal('primary')}
                      title="Assign Primary"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span className="sm:inline">Assign Primary</span>
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <div className="text-slate-600 dark:text-slate-300 sm:col-span-2">Secondary:</div>
                  <div className="min-w-0 sm:col-span-7" />
                  <div className="sm:col-span-3 flex sm:justify-end gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-10 rounded-xl flex items-center justify-center gap-1.5 shrink-0"
                      onClick={() => openAssignModal('secondary')}
                      title="Add Secondary"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span className="sm:inline">Add Secondary</span>
                    </Button>
                  </div>
                </div>
                {(detailClass.secondary_teacher_platform_user_ids ?? []).length === 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center pl-0 sm:pl-[calc(2/12*100%)]">
                    <div className="min-w-0 sm:col-span-7 sm:col-start-3 text-gray-500 dark:text-gray-400">None</div>
                  </div>
                ) : (
                  <div className="space-y-2 pl-0 sm:pl-[calc(2/12*100%)]">
                    {(detailClass.secondary_teacher_platform_user_ids ?? []).map((pid) => (
                      <div key={pid} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                        <div className="min-w-0 sm:col-span-7 sm:col-start-3 font-medium text-gray-900 dark:text-white break-words">
                          {teacherNameByPlatformId[pid] ?? pid}
                        </div>
                        <div className="sm:col-span-3 flex sm:justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                            title="Remove"
                            onClick={() => void handleUnassign(pid)}
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        title={assignRole === 'primary' ? 'Assign Primary Teacher' : 'Add Secondary Teacher'}
        size="sm"
      >
        <form onSubmit={handleAssignSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teacher</label>
            <Select
              options={[
                { label: 'Select teacher…', value: '' },
                ...teachers
                  .filter((t) => t.platform_user_id)
                  .map((t) => ({
                    label: t.display_name || `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email || t.id,
                    value: t.id,
                  })),
              ]}
              value={assignTeacherId}
              onChange={(e) => setAssignTeacherId(e.target.value)}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Only teachers with an active platform link can be assigned.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setIsAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isAssigning} disabled={!assignTeacherId.trim()}>
              Assign as {assignRole === 'primary' ? 'Primary' : 'Secondary'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditClassId(null);
          setEditName('');
          setEditNameError('');
        }}
        title="Edit class"
        size="sm"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <Input
            label="Name"
            required
            value={editName}
            error={editNameError}
            onChange={(e) => {
              const v = e.target.value;
              setEditName(v);
              const result = validateGradeName(v);
              setEditNameError(result.valid ? '' : result.message ?? '');
            }}
            placeholder="Class name"
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditClassId(null);
                setEditName('');
              }}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={isPatching}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};
